import React from 'react';
import classNames from 'classnames';

import { LinkedLogo, NamedLink } from '../../../../components';
import { FormattedMessage } from 'react-intl';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import SectionContainer from '../SectionContainer';

import css from './SectionFooter.module.css';
import { ReactComponent as Logo } from '../../../../assets/svg/bizzprofiles.svg';
import { ReactComponent as Secured } from '../../../../assets/svg/securedIcon.svg';
import { useTranslation } from '../../../../hooks/useTranslation';
import { cloneDeep } from 'lodash';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const prepareBlockEntires = blocks => {
  const entries = blocks.reduce((acc, block) => {
    const content = block.text.content;
    const result = content.match(/\[[^\]]+\]|\([^)]+\)/g);
    if (result == null) {
      acc[content] = content;
      return acc;
    }
    for (let i = 0; i < result.length; i += 2) {
      acc[result[i + 1]] = result[i];
    }
    return acc;
  }, {});
  return entries;
};

const prepareBlockArray = (blocks, blockEntires) => {
  const arr = blocks.map(block => {
    const newBlock = cloneDeep(block);
    const content = newBlock.text.content;
    const result = content.match(/\[[^\]]+\]|\([^)]+\)/g);
    if (result == null) {
      newBlock.text = { ...newBlock.text, content: blockEntires[content] };
      return newBlock;
    }
    const finalResults = [];
    for (let i = 0; i < result.length; i += 2) {
      const key = result[i + 1];
      result[i] = blockEntires[key];
      finalResults.push(blockEntires[key] + key);
    }
    newBlock.text = { ...newBlock.text, content: '- ' + finalResults.join('\n- ') };
    return newBlock;
  });

  return arr;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  const blocksContentEntries = prepareBlockEntires(blocks);
  const { translation: trEntries } = useTranslation({
    batchId: sectionId,
    translationEntries: blocksContentEntries,
  });
  const updatedBlocks = prepareBlockArray(blocks, trEntries);

  //console.log(text, text.content.split(/\[(.*?)\]/));

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          {/* <div>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
          </div> */}
          <NamedLink name="LandingPage">
            <Logo />
          </NamedLink>
          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
            {/* {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null} */}
            <div className={css.sectionCertified}>
              <div className={css.sectionSecured}>
                <Secured />
                <FormattedMessage id="SectionFooter.siteSecured" />
              </div>
              <FormattedMessage id="SectionFooter.sslCertified" />
            </div>
            <Field data={copyright} className={css.copyright} />
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            <BlockBuilder blocks={updatedBlocks} sectionId={sectionId} options={options} />
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
